import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import api from '@/services/api'
import router from '@/router'

interface User {
  user_id: number
  email: string
  first_name: string
  last_name: string
  brand_id: number
  brand_name: string
  role: string
  status: string
  mobile?: string
  house_no?: string
  street?: string
  barangay?: string
  city?: string
}

interface LoginCredentials {
  email: string
  password: string
  brand_id: number
}

interface AdminLoginCredentials {
  email: string
  password: string
}

interface AuthResponse {
  token: string
  user: User
}

interface ApiError {
  message: string
  status: number
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('token'))
  const loading = ref(false)
  const error = ref<string | null>(null)
  // null = admin mode; number = customer view for that brand ID
  const adminViewBrandId = ref<number | null>(
    JSON.parse(localStorage.getItem('adminViewBrandId') ?? 'null')
  )
  // Admin's chosen brand for their own panel theming (persisted across sessions)
  // Default: 1 (Continental) on first login; saved to localStorage after first selection
  const adminBrandId = ref<number>(
    JSON.parse(localStorage.getItem('adminBrandId') ?? '1')
  )

  // Getters
  const isAuthenticated = computed(() => !!token.value)
  const currentUser = computed(() => user.value)
  const isLoading = computed(() => loading.value)
  const hasError = computed(() => !!error.value)
  const isAdminViewMode = computed(() => adminViewBrandId.value !== null)
  const isInCustomerView = computed(() => user.value?.role === 'admin' && adminViewBrandId.value !== null)

  // Actions
  async function login(credentials: LoginCredentials) {
    loading.value = true
    error.value = null
    const toast = useToast()

    try {
      const response = await api.post<AuthResponse>('/auth/login', credentials)
      const { token: newToken, user: userData } = response.data

      token.value = newToken
      user.value = userData

      localStorage.setItem('token', newToken)

      // Show success toast
      toast.success('Login successful!', {
        timeout: 3000
      })

      await router.push('/dashboard')
    } catch (err: any) {
      // Handle Laravel validation errors
      if (err.response?.data?.message) {
        error.value = err.response.data.message
      } else {
        error.value = 'Failed to login. Please try again.'
      }
      throw err
    } finally {
      loading.value = false
    }
  }

  async function adminLogin(credentials: AdminLoginCredentials) {
    loading.value = true
    error.value = null
    const toast = useToast()

    try {
      const response = await api.post<AuthResponse>('/admin/login', credentials)
      const { token: newToken, user: userData } = response.data

      token.value = newToken
      user.value = userData

      localStorage.setItem('token', newToken)

      // Show success toast
      toast.success('Admin login successful!', {
        timeout: 3000
      })

      // Redirect to admin dashboard
      await router.push('/dashboard/admin')
    } catch (err: any) {
      // Handle Laravel validation errors
      if (err.response?.data?.message) {
        error.value = err.response.data.message
      } else {
        error.value = 'Failed to login. Please try again.'
      }
      throw err
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    loading.value = true
    error.value = null
    const toast = useToast()

    // Check if user is admin before clearing user data
    const isAdmin = user.value?.role === 'admin'

    try {
      await api.post('/auth/logout')
      // Show success toast
      toast.success('Logged out successfully!', {
        timeout: 3000
      })
    } catch (err) {
      const apiError = err as ApiError
      error.value = apiError.message || 'Failed to logout. Please try again.'
      console.error('Logout error:', err)
    } finally {
      token.value = null
      user.value = null
      localStorage.removeItem('token')
      adminViewBrandId.value = null
      localStorage.removeItem('adminViewBrandId')

      // Redirect based on user role
      if (isAdmin) {
        router.push('/admin/login')
      } else {
        router.push('/brands')
      }

      loading.value = false
    }
  }

  async function checkAuth() {
    if (!token.value) return false

    // If user is already loaded, no need to check again
    if (user.value) return true

    loading.value = true
    error.value = null

    try {
      // Call the /auth/me endpoint to verify token and get user data
      const response = await api.get<{ user: User }>('/auth/me')
      user.value = response.data.user

      return true
    } catch (err) {
      const apiError = err as ApiError
      error.value = apiError.message || 'Session expired. Please login again.'
      token.value = null
      user.value = null
      localStorage.removeItem('token')
      adminViewBrandId.value = null
      localStorage.removeItem('adminViewBrandId')
      return false
    } finally {
      loading.value = false
    }
  }

  function updateUserData(userData: User) {
    user.value = userData
  }

  function switchToCustomerView(brandId: number) {
    if (user.value?.role !== 'admin') return
    adminViewBrandId.value = brandId
    localStorage.setItem('adminViewBrandId', JSON.stringify(brandId))
  }

  function switchToAdminView() {
    adminViewBrandId.value = null
    localStorage.removeItem('adminViewBrandId')
  }

  function setAdminBrand(brandId: number) {
    if (user.value?.role !== 'admin') return
    adminBrandId.value = brandId
    localStorage.setItem('adminBrandId', JSON.stringify(brandId))
  }

  function clearError() {
    error.value = null
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    adminViewBrandId,
    adminBrandId,
    // Getters
    isAuthenticated,
    currentUser,
    isLoading,
    hasError,
    isAdminViewMode,
    isInCustomerView,
    // Actions
    login,
    adminLogin,
    logout,
    checkAuth,
    updateUserData,
    switchToCustomerView,
    switchToAdminView,
    setAdminBrand,
    clearError
  }
})
